import { faEllipsisVertical } from "@fortawesome/pro-light-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { VaporToolbar } from "@vapor/react-custom";
import Typography from "@vapor/react-extended/ExtendedTypography";
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Divider,
    FormHelperText,
    IconButton,
    Menu,
    MenuItem,
    Stack,
    TextareaAutosize,
    useTheme,
} from "@vapor/react-material";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDeleteActivityNote } from "../../../../../core/api/use-delete-activity-note/useDeleteActivityNote";
import type { FulfilmentActivityDto } from "../../../../../core/api/use-get-fulfilment-activities/FulfilmentActivityDto";
import { FulfilmentStatus } from "../../../../../core/api/use-get-fulfilment-detail/FulfilmentStatus";
import { useUpdateActivity } from "../../../../../core/api/use-update-activity/useUpdateActivity";
import { useSuccessNotification } from "../../../../../core/hooks/useSuccessNotification";
import { usePermission } from "../../../../../core/permissions/usePermission";
import { MAX_NOTE_LENGTH } from "../add-activity/AddActivity";

interface FulfimentActivityNoteProps {
    activity: FulfilmentActivityDto;
    fulfilmentStatus?: FulfilmentStatus;
    levyId: string;
    onUpdateNote: () => unknown;
    taxPayerWorkspaceId: string;
    year: number;
}

export const FulfimentActivityNote: React.FC<FulfimentActivityNoteProps> = ({
    activity,
    fulfilmentStatus,
    levyId,
    onUpdateNote,
    taxPayerWorkspaceId,
    year,
}): JSX.Element => {
    const { t } = useTranslation();
    const { showTextMessage } = useSuccessNotification();
    const theme = useTheme();
    const canManageActivities = usePermission("activityManage");

    const [data, setData] = useState<string | undefined>(activity.notes);
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] =
        useState<boolean>(false);
    const [menuAnchorEl, setMenuAnchorEl] = useState<
        (EventTarget & HTMLButtonElement) | null
    >(null);
    const [showEditingMode, setShowEditingMode] = useState<boolean>(false);

    const updateActivityMutation = useUpdateActivity();
    const deleteActivityNoteMutation = useDeleteActivityNote();

    const isDataValid = useMemo(() => {
        return !!data && data.length <= MAX_NOTE_LENGTH;
    }, [data]);

    const handleCloseMenu = (event: React.MouseEvent<HTMLElement>) => {
        event.stopPropagation();
        setMenuAnchorEl(null);
    };

    const handleDeleteNote = async () => {
        await deleteActivityNoteMutation.mutateAsync({
            activityId: activity.id,
            taxPayerWorkspaceId: taxPayerWorkspaceId,
            levyId: levyId,
            year: year,
        });
        showTextMessage(
            t(
                "views.fulfilment_detail.components.edit_activity.update_note.delete.successfull",
            ),
        );
        onUpdateNote();
    };

    const handleUpdateNote = async () => {
        await updateActivityMutation.mutateAsync({
            activityId: activity.id,
            notes: data,
            taxPayerWorkspaceId: taxPayerWorkspaceId,
            levyId: levyId,
            year: year,
        });
        showTextMessage(
            t(
                "views.fulfilment_detail.components.edit_activity.update_note.successfull",
            ),
        );
        onUpdateNote();
    };

    const onClickCancel = () => {
        setShowEditingMode(false);
    };

    const onClickCancelDialog = () => {
        setIsDeleteDialogOpen(false);
    };

    const onClickMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation();
        setMenuAnchorEl(event.currentTarget);
    };

    const onClickShowDeleteNoteDialog = () => {
        setMenuAnchorEl(null);
        setIsDeleteDialogOpen(true);
    };

    return (
        <Stack p={3}>
            <Stack gap={1} justifyContent="space-between">
                <Box>
                    <Typography variant={"body500"}>
                        {t(
                            "views.fulfilment_detail.components.add_activity.note.label",
                        )}
                    </Typography>
                    {showEditingMode ? (
                        <Stack gap={1}>
                            <TextareaAutosize
                                disabled={updateActivityMutation.isPending}
                                error={!!data && data.length > MAX_NOTE_LENGTH}
                                onChange={(event) => {
                                    setData(event.target.value);
                                }}
                                minRows={3}
                                placeholder={t(
                                    "views.fulfilment_detail.components.add_activity.note.placeholder",
                                )}
                                style={{
                                    borderColor:
                                        !!data && data.length > MAX_NOTE_LENGTH
                                            ? theme.palette.primary
                                                  .accentRustyRed
                                            : undefined,
                                }}
                                value={data}
                            />
                            {data && data.length > MAX_NOTE_LENGTH && (
                                <FormHelperText>
                                    {t(
                                        "views.fulfilment_detail.components.edit_activity.update_note.note.text_helper",
                                        { number: MAX_NOTE_LENGTH },
                                    )}
                                </FormHelperText>
                            )}
                        </Stack>
                    ) : (
                        <Typography padding={1}>{activity.notes}</Typography>
                    )}
                </Box>
            </Stack>
            <Dialog open={isDeleteDialogOpen} onClose={onClickCancelDialog}>
                <DialogTitle>
                    {t(
                        "views.fulfilment_detail.components.edit_activity.update_note.delete.dialog.title",
                    )}
                </DialogTitle>
                <Divider variant="fullWidth" />
                <DialogContent>
                    <DialogContentText>
                        {t(
                            "views.fulfilment_detail.components.edit_activity.update_note.delete.dialog.content",
                        )}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button
                        disabled={deleteActivityNoteMutation.isPending}
                        onClick={onClickCancelDialog}
                        variant="outlined"
                    >
                        {t(
                            "views.fulfilment_detail.components.edit_activity.update_note.delete.dialog.actions.cancel",
                        )}
                    </Button>
                    <Button
                        disabled={deleteActivityNoteMutation.isPending}
                        color="error"
                        onClick={handleDeleteNote}
                        variant="contained"
                    >
                        {t(
                            "views.fulfilment_detail.components.edit_activity.update_note.delete.dialog.actions.confirm",
                        )}
                        {deleteActivityNoteMutation.isPending && (
                            <CircularProgress
                                color="inherit"
                                sx={{ ml: 1 }}
                                size={16}
                            />
                        )}
                    </Button>
                </DialogActions>
            </Dialog>
            <Box
                sx={{
                    ml: -3,
                }}
            >
                {showEditingMode && canManageActivities ? (
                    <VaporToolbar
                        contentRight={[
                            <Button
                                disabled={
                                    !isDataValid ||
                                    updateActivityMutation.isPending
                                }
                                onClick={handleUpdateNote}
                                size="large"
                                variant="contained"
                            >
                                {t(
                                    "views.fulfilment_detail.components.fulfilment_detail.activity.note.save",
                                )}
                                {updateActivityMutation.isPending && (
                                    <CircularProgress
                                        color="inherit"
                                        sx={{ ml: 1 }}
                                        size={16}
                                    />
                                )}
                            </Button>,
                        ]}
                        contentLeft={[
                            <Button
                                disabled={updateActivityMutation.isPending}
                                onClick={onClickCancel}
                                size="large"
                                variant="outlined"
                            >
                                {t(
                                    "views.fulfilment_detail.components.fulfilment_detail.activity.note.cancel",
                                )}
                            </Button>,
                        ]}
                        size="large"
                        variant="regular"
                    />
                ) : (
                    <>
                        {fulfilmentStatus &&
                            fulfilmentStatus !== FulfilmentStatus.COMPLETED &&
                            canManageActivities && (
                                <VaporToolbar
                                    contentRight={[
                                        <Stack
                                            direction="row"
                                            justifyContent="flex-end"
                                        >
                                            <IconButton
                                                aria-controls="menu"
                                                aria-haspopup="true"
                                                onClick={onClickMenu}
                                                size="small"
                                            >
                                                <FontAwesomeIcon
                                                    color={
                                                        theme.palette.primary
                                                            .interactiveDefault
                                                    }
                                                    icon={faEllipsisVertical}
                                                    size="xl"
                                                    fontWeight={400}
                                                />
                                            </IconButton>
                                            <Menu
                                                id="menu"
                                                anchorEl={menuAnchorEl}
                                                open={!!menuAnchorEl}
                                                onClose={handleCloseMenu}
                                                anchorOrigin={{
                                                    vertical: "bottom",
                                                    horizontal: "center",
                                                }}
                                                transformOrigin={{
                                                    vertical: "top",
                                                    horizontal: "right",
                                                }}
                                            >
                                                <MenuItem
                                                    onClick={
                                                        onClickShowDeleteNoteDialog
                                                    }
                                                >
                                                    <Stack
                                                        direction="row"
                                                        justifyContent="center"
                                                        spacing={1}
                                                    >
                                                        <Typography>
                                                            {t(
                                                                "views.fulfilment_detail.components.edit_activity.update_note.delete",
                                                            )}
                                                        </Typography>
                                                    </Stack>
                                                </MenuItem>
                                            </Menu>
                                        </Stack>,
                                        <Button
                                            onClick={() =>
                                                setShowEditingMode(true)
                                            }
                                            size="large"
                                            variant="contained"
                                        >
                                            {t(
                                                "views.fulfilment_detail.components.fulfilment_detail.activity.note.edit",
                                            )}
                                        </Button>,
                                    ]}
                                    size="large"
                                    variant="regular"
                                />
                            )}
                    </>
                )}
            </Box>
        </Stack>
    );
};
